/**
 * ACU Usage Ledger - Per-user sequence number tracking for ACU usage persistence.
 *
 * Mirrors SyncLedger but tracks ACU consumption sequences per user (not per project).
 * Sequences are persisted in localStorage synchronously before any async operation begins.
 *
 * SEQUENCE OWNERSHIP:
 *   Sequence numbers are owned here and persisted in localStorage so they survive
 *   reloads. They are NEVER stored only in runtime memory. Every call to nextSequence()
 *   immediately writes to localStorage before returning.
 *
 * Storage key: nuvra-acu-usage-ledger
 */

import { logger } from "@/system/logger";

const LEDGER_KEY = "nuvra-acu-usage-ledger";

export type ACULedgerStatus = "synced" | "pending" | "failed";

export interface ACUUsageLedgerEntry {
  userId:               string;
  localSequenceNum:     number; // last sequence minted
  cloudSequenceNum:     number; // last sequence confirmed synced to cloud
  lastSyncedAt:         number; // epoch ms, 0 if never
  status:               ACULedgerStatus;
  pendingCount:         number; // sequences minted but not yet confirmed in cloud
  consecutiveFailures:  number;
  lastError:            string | null;
}

type LedgerStore = Record<string, ACUUsageLedgerEntry>;

/* =========================================================
   INTERNAL HELPERS
   ========================================================= */

function readStore(): LedgerStore {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(LEDGER_KEY);
    return raw ? (JSON.parse(raw) as LedgerStore) : {};
  } catch {
    return {};
  }
}

function writeStore(store: LedgerStore): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(LEDGER_KEY, JSON.stringify(store));
  } catch (err) {
    logger.error("ACUUsageLedger write failed", { err });
  }
}

function defaultEntry(userId: string): ACUUsageLedgerEntry {
  return {
    userId,
    localSequenceNum:    0,
    cloudSequenceNum:    0,
    lastSyncedAt:        0,
    status:              "synced",
    pendingCount:        0,
    consecutiveFailures: 0,
    lastError:           null,
  };
}

/* =========================================================
   ACU USAGE LEDGER API
   ========================================================= */

export const ACUUsageLedger = {

  /** Get current ledger entry for a user, initialising if absent. */
  get(userId: string): ACUUsageLedgerEntry {
    const store = readStore();
    if (!store[userId]) {
      store[userId] = defaultEntry(userId);
      writeStore(store);
    }
    return { ...store[userId] };
  },

  /**
   * Increment local sequence number and persist immediately.
   * Returns the NEW sequence number assigned to this ACU usage event.
   *
   * Must be called synchronously before any async operation on the entry
   * to satisfy the "sequences never exist only in runtime memory" constraint.
   */
  nextSequence(userId: string): number {
    const store = readStore();
    if (!store[userId]) store[userId] = defaultEntry(userId);
    const entry = store[userId];
    entry.localSequenceNum += 1;
    entry.pendingCount = entry.localSequenceNum - entry.cloudSequenceNum;
    entry.status = "pending";
    store[userId] = entry;
    writeStore(store);
    logger.debug("ACUUsageLedger: sequence minted", { userId, sequenceNum: entry.localSequenceNum });
    return entry.localSequenceNum;
  },

  /** Mark a sequence number as successfully synced to cloud. */
  markSynced(userId: string, sequenceNum: number): void {
    const store = readStore();
    if (!store[userId]) return;
    const entry = store[userId];
    if (sequenceNum > entry.cloudSequenceNum) {
      entry.cloudSequenceNum = sequenceNum;
      entry.lastSyncedAt     = Date.now();
    }
    entry.pendingCount        = Math.max(0, entry.localSequenceNum - entry.cloudSequenceNum);
    entry.status              = entry.pendingCount === 0 ? "synced" : "pending";
    entry.consecutiveFailures = 0;
    entry.lastError           = null;
    store[userId] = entry;
    writeStore(store);
  },

  /** Record a sync failure. Increments consecutiveFailures counter. */
  markFailed(userId: string, error: string): void {
    const store = readStore();
    if (!store[userId]) return;
    store[userId].consecutiveFailures += 1;
    store[userId].lastError            = error;
    store[userId].status               = "failed";
    writeStore(store);
  },

  /** All ledger entries across all users. */
  getAll(): ACUUsageLedgerEntry[] {
    return Object.values(readStore());
  },

  /** Users with local sequences that exceed their cloud sequence. */
  getPending(): ACUUsageLedgerEntry[] {
    return Object.values(readStore()).filter(e => e.localSequenceNum > e.cloudSequenceNum);
  },
};
